/**
 * Delete share (plan Task 7.16, §3 IPC `share-delete`): the app-level confirm with the typed-name gate, its copy naming
 * the snapshots that go with the share and the share soft-delete state (on: restorable for N days from the Azure
 * portal; off: cannot be undone; unknown: the reason). The request carries the count the user confirmed; a
 * `SnapshotsChanged` refusal keeps the confirm open with a Reload that re-reads the count. The confirm's latch keeps
 * one delete in flight. On success every tab on the share (live or snapshot) closes and its pins go; the tree
 * refreshes through main's `azure-storage:mutated` event. Microsoft Entra is attempted (I2).
 */
import { useEffect } from 'react';
import type { FileShareProtocolState, StorageOperationError, StorageRecoverabilityState } from '../../../../../types/azureStorage';
import { openConfirmDialog } from '../../../common/components/confirm/confirmDialogStore';
import { toStorageOperationError } from '../../services/azureStorageApi';
import type { ShareActionSource } from '../namedShareActions';
import { startShareRequest } from '../shareApi';
import { closeShareSurfaces, shareActionOutcome, trackShareAction } from '../shareActions';
import { ShareErrorNotice } from './ShareErrorNotice';
import { createShareDeleteSession, type ShareDeleteFacts, type ShareDeleteSession } from './shareDeleteSession';

const plural = (count: number) => `${count} snapshot${count === 1 ? '' : 's'}`;

function countSentence(share: string, facts: ShareDeleteFacts): string {
  if (facts.status === 'loading') return `Reading the snapshots of ${share}…`;
  if (facts.count === 0) return `Deletes ${share} and every file in it; the share has no snapshots.`;
  if (facts.count !== undefined) return `Deletes ${share}, every file in it and its ${plural(facts.count)}.`;
  return `Deletes ${share}, every file in it and its snapshots. Jam SQL could not count the snapshots: ${facts.countReason ?? facts.readError?.message ?? 'unknown reason'}`;
}

function softDeleteSentence(state: StorageRecoverabilityState): string {
  if (state.state === 'on') return `Share soft delete is on: the share can be restored for ${state.days} days from the Azure portal.`;
  if (state.state === 'off') return 'Share soft delete is off: this cannot be undone.';
  return `Whether the share can be restored is unknown: ${state.reason}`;
}

function DeleteShareSummary({ share, session }: { share: string; session: ShareDeleteSession }) {
  const facts = session.store();
  useEffect(() => {
    session.ensureLoaded();
    return () => session.dispose();
  }, [session]);
  const error = facts.error ?? (facts.status === 'failed' ? facts.readError : undefined);
  return (
    <span className="block space-y-2" data-testid="delete-share-summary">
      <span className="block" data-testid="delete-share-counts">{countSentence(share, facts)}</span>
      {facts.softDelete && <span className="block" data-testid="delete-share-soft-delete">{softDeleteSentence(facts.softDelete)}</span>}
      {error && <ShareErrorNotice error={error} testId="delete-share-error" onReload={session.reload} />}
    </span>
  );
}

const NOT_COUNTED: StorageOperationError = {
  category: 'invalid_input', code: 'SnapshotCountUnknown', retryable: false, fix: { kind: 'reload' },
  message: 'Jam SQL cannot delete this share without knowing how many snapshots go with it. Reload, or delete it from a connection that can list its snapshots.',
};

export function openShareDeleteConfirm(args: { connectionId: string; share: string; source: ShareActionSource; protocol?: FileShareProtocolState }): void {
  const session = createShareDeleteSession(args);
  openConfirmDialog({
    title: 'Delete file share',
    description: <DeleteShareSummary share={args.share} session={session} />,
    confirmLabel: 'Delete share',
    variant: 'destructive',
    requireTypedName: { value: args.share, label: `Type the share name “${args.share}” to confirm` },
    onConfirm: async () => {
      await session.loaded();
      const { count, protocol } = session.store.getState();
      if (count === undefined) throw NOT_COUNTED;
      session.store.setState({ error: undefined });
      const result = await startShareRequest('shareDelete', { connectionId: args.connectionId, resource: { kind: 'share', share: args.share }, confirmedSnapshotCount: count }).promise;
      trackShareAction('delete', shareActionOutcome(result.ok ? undefined : result.error), { source: args.source, protocol });
      if (!result.ok) throw result.error;
      await closeShareSurfaces(args.connectionId, args.share);
    },
    onError: (error) => session.store.setState({ error: toStorageOperationError(error) }),
  });
}
